// src/app/components/atoms/CalendarAtom.tsx
import React, { forwardRef, useEffect, useState, useCallback } from 'react';
import { Calendar as PrimeCalendar, CalendarProps as PrimeCalendarProps } from 'primereact/calendar';
import './CalendarAtom.scss';

// Base props without the conflicting types
type BaseCalendarProps = Omit<PrimeCalendarProps, 'value' | 'onChange' | 'selectionMode'>;

export interface CalendarAtomProps extends BaseCalendarProps {
  /** Current value - can be Date, Date[], or null depending on selectionMode */
  value?: Date | Date[] | null;
  /** Formik‑style onChange (receives a DOM‑like event) */
  onChange?: (e: React.ChangeEvent<any>) => void;
  /** Stand‑alone callback for backward compatibility */
  onDateChange?: (date: Date | Date[] | null) => void;
  /** Selection mode */
  selectionMode?: 'single' | 'multiple' | 'range';
  /** Label to display above the calendar */
  label?: string;
  /** Error messages to display */
  errors?: string[];
  /** Additional CSS classes */
  className?: string;
  /** Formik name attribute */
  name?: string;
  /** Formik blur handler */
  onBlur?: React.FocusEventHandler<any>;
  /** Show trigger icon by default */
  showIcon?: boolean;
  /** Date format */
  dateFormat?: string;
  /** Whether the calendar should be inline */
  inline?: boolean;
  /** Minimum selectable date */
  minDate?: Date;
  /** Maximum selectable date */
  maxDate?: Date;
  /** Disabled dates */
  disabledDates?: Date[];
  /** Disabled days of week (0–6) */
  disabledDays?: number[];
  /** View mode */
  view?: 'date' | 'month' | 'year';
  /** Number of months to display */
  numberOfMonths?: number;
  /** Placeholder text */
  placeholder?: string;
  /** Read‑only mode */
  readOnly?: boolean;
  /** Disabled state */
  disabled?: boolean;
}

const CalendarAtom = forwardRef<HTMLDivElement, CalendarAtomProps>(
  (
    {
      value = null,
      onChange,
      onDateChange,
      onBlur,
      name,
      label,
      errors = [],
      className = '',
      showIcon = true,
      // PrimeReact 10's default trigger icon is an SVG, which this atom's stylesheet
      // hides (the trigger is restyled as a PrimeIcons glyph button) — so the atom
      // supplies the glyph itself, as TimePickerAtom does with `pi pi-clock`.
      icon = 'pi pi-calendar',
      dateFormat = 'mm/dd/yy',
      selectionMode = 'single',
      inline = false,
      minDate,
      maxDate,
      disabledDates,
      disabledDays,
      view = 'date',
      numberOfMonths = 1,
      placeholder,
      readOnly = false,
      disabled = false,
      ...restProps
    },
    ref
  ) => {
    // Hide the trigger icon whenever inline mode is on
    const effectiveShowIcon = inline ? false : showIcon;

    // Controlled internal state
    const [internalValue, setInternalValue] = useState<Date | Date[] | null>(value);

    useEffect(() => {
      setInternalValue(value ?? null);
    }, [value]);

    // Normalize to midnight
    const normalizeDate = useCallback((date: Date): Date => {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }, []);

    // PrimeReact change handler
    const handlePrimeChange = useCallback(
      (e: any) => {
        let pickedValue = e.value as Date | Date[] | null;
        if (pickedValue && !restProps.timeOnly) {
          pickedValue = Array.isArray(pickedValue)
            ? pickedValue.map(normalizeDate)
            : normalizeDate(pickedValue);
        }
        setInternalValue(pickedValue);

        if (onChange) {
          // synthesize a Formik‑style event
          const syntheticEvent = {
            target: { name, value: pickedValue },
            currentTarget: { name, value: pickedValue },
          } as React.ChangeEvent<any>;
          onChange(syntheticEvent);
        }
        if (onDateChange) {
          onDateChange(pickedValue);
        }
      },
      [name, onChange, onDateChange, normalizeDate, restProps.timeOnly]
    );

    const hasError = errors.length > 0;
    const wrapperClasses = [
      'calendar-atom',
      className,
      hasError && 'calendar-atom--error',
      inline && 'calendar-atom--inline',
    ]
      .filter(Boolean)
      .join(' ');

    const calendarClasses = ['calendar-atom__input', hasError && 'calendar-atom__input--error']
      .filter(Boolean)
      .join(' ');

    const renderCalendar = () => {
      const commonProps = {
        id: name,
        name,
        onChange: handlePrimeChange,
        onBlur,
        className: calendarClasses,
        showIcon: effectiveShowIcon,
        icon,
        dateFormat,
        inline,
        minDate,
        maxDate,
        disabledDates,
        disabledDays,
        view,
        numberOfMonths,
        placeholder,
        readOnlyInput: readOnly,
        disabled,
        inputId: name,
        ...restProps,
      };

      if (selectionMode === 'multiple') {
        return <PrimeCalendar {...commonProps} selectionMode="multiple" value={internalValue as Date[] | null} />;
      } else if (selectionMode === 'range') {
        return <PrimeCalendar {...commonProps} selectionMode="range" value={internalValue as Date[] | null} />;
      } else {
        return <PrimeCalendar {...commonProps} selectionMode="single" value={internalValue as Date | null} />;
      }
    };

    return (
      <div className={wrapperClasses} ref={ref}>
        {label && (
          <label className="calendar-atom__label" htmlFor={name}>
            {label}
          </label>
        )}

        {renderCalendar()}

        {hasError && (
          <ul className="calendar-atom__errors">
            {errors.map((error, index) => (
              <li key={`${error}-${index}`} className="calendar-atom__error">
                {error}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
);

CalendarAtom.displayName = 'CalendarAtom';
export default CalendarAtom;
